import type {
  OperatorIdentity,
  OrderDetail,
  StorePackageOrderItem,
  StorePackageRecord,
} from "@billiard/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, PackageCheck, PackagePlus, RefreshCw, RotateCcw } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  ApiRequestError,
  addStorePackageOrderItem,
  cancelUnpaidStorePackageOrderItem,
  getStorePackageAddResult,
  getStorePackageReturnResult,
  listStorePackages,
} from "./api";
import { businessErrorMessage } from "./businessErrorMessage";

type PackageOperationRecovery =
  | {
      kind: "ADD";
      actorId: string;
      sessionId: string;
      packageName: string;
      command: {
        packageId: string;
        expectedPackageVersion: number;
        expectedOrderVersion: number;
        idempotencyKey: string;
      };
    }
  | {
      kind: "CANCEL";
      actorId: string;
      sessionId: string;
      packageName: string;
      packageItemId: string;
      command: {
        expectedOrderVersion: number;
        reason: string;
        idempotencyKey: string;
      };
    };

type PackageOperationRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: PackageOperationRecovery }
  | { status: "UNAVAILABLE" };

function formatFen(value: number): string {
  return `¥${(value / 100).toFixed(2)}`;
}

function recoveryKey(operator: OperatorIdentity): string {
  return `billiard:store-package-order:v1:${operator.tenantId}:${operator.storeId}:${operator.id}`;
}

function isPackageOperationRecovery(
  value: unknown,
  operator: OperatorIdentity,
): value is PackageOperationRecovery {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<PackageOperationRecovery>;
  if (
    candidate.actorId !== operator.id ||
    typeof candidate.sessionId !== "string" ||
    candidate.sessionId.length === 0 ||
    typeof candidate.packageName !== "string" ||
    candidate.packageName.length === 0 ||
    candidate.command === undefined ||
    typeof candidate.command !== "object" ||
    candidate.command === null ||
    typeof candidate.command.idempotencyKey !== "string" ||
    candidate.command.idempotencyKey.length === 0 ||
    !Number.isSafeInteger(candidate.command.expectedOrderVersion) ||
    candidate.command.expectedOrderVersion < 1
  ) {
    return false;
  }
  if (candidate.kind === "ADD") {
    return (
      typeof candidate.command.packageId === "string" &&
      candidate.command.packageId.length > 0 &&
      Number.isSafeInteger(candidate.command.expectedPackageVersion) &&
      candidate.command.expectedPackageVersion >= 1
    );
  }
  return (
    candidate.kind === "CANCEL" &&
    typeof candidate.packageItemId === "string" &&
    candidate.packageItemId.length > 0 &&
    typeof candidate.command.reason === "string" &&
    candidate.command.reason.trim().length >= 2
  );
}

function readRecovery(operator: OperatorIdentity): PackageOperationRecoveryView {
  try {
    const raw = localStorage.getItem(recoveryKey(operator));
    if (!raw) return { status: "NONE" };
    const value: unknown = JSON.parse(raw);
    return isPackageOperationRecovery(value, operator)
      ? { status: "OWNED", recovery: value }
      : { status: "UNAVAILABLE" };
  } catch {
    return { status: "UNAVAILABLE" };
  }
}

export function storePackageOperationRecoveryBlocksOrderWrites(
  operator: OperatorIdentity,
): boolean {
  return readRecovery(operator).status !== "NONE";
}

function isTerminalPackageMutationError(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    [400, 403, 404, 409].includes(error.statusCode ?? 0) &&
    !error.code.endsWith("RESULT_UNKNOWN") &&
    error.code !== "GATEWAY_RESPONSE_INVALID"
  );
}

function componentSummary(item: StorePackageOrderItem | StorePackageRecord): string {
  return item.components.map((component) => `${component.name} × ${component.quantity}`).join("、");
}

export function StorePackageOrderPanel({
  operator,
  effectiveRoles,
  order,
  expectedOrderVersion,
  disabled,
  onRecoveryLockedChange,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  order: OrderDetail;
  expectedOrderVersion: number | null;
  disabled: boolean;
  onRecoveryLockedChange?: (locked: boolean) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const titleId = useId();
  const canOperate = effectiveRoles.some((role) => role === "CASHIER" || role === "MANAGER");
  const packagesQuery = useQuery({
    queryKey: ["store-packages", "enabled", operator.tenantId, operator.storeId],
    queryFn: () => listStorePackages(false),
    enabled: canOperate,
    retry: false,
  });
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackKind, setFeedbackKind] = useState<"INFO" | "SUCCESS" | "ERROR">("INFO");
  const [recoveryView, setRecoveryView] = useState<PackageOperationRecoveryView>(() =>
    readRecovery(operator),
  );
  const recovery = recoveryView.status === "OWNED" ? recoveryView.recovery : null;
  const [cancelDraft, setCancelDraft] = useState<{ itemId: string; reason: string } | null>(null);

  useEffect(() => {
    const restored = readRecovery(operator);
    setRecoveryView(restored);
    if (restored.status === "OWNED") {
      setFeedbackKind("INFO");
      setFeedback(
        restored.recovery.sessionId === order.sessionId
          ? `“${restored.recovery.packageName}”上次操作结果尚未确认。请先查询原结果，不要重复加套餐或恢复库存。`
          : "另一张台单有门店套餐操作结果待确认；请回到原台单处理后再继续。",
      );
    } else if (restored.status === "UNAVAILABLE") {
      setFeedbackKind("ERROR");
      setFeedback(
        "本机无法安全读取套餐防重复记录。为避免重复扣库存或改金额，当前台单修改和收款已暂停；请恢复浏览器存储后重新打开台单。",
      );
    }
  }, [operator, order.sessionId]);

  useEffect(() => {
    onRecoveryLockedChange?.(recoveryView.status !== "NONE");
  }, [onRecoveryLockedChange, recoveryView.status]);

  const clearRecovery = (): boolean => {
    try {
      localStorage.removeItem(recoveryKey(operator));
      setRecoveryView({ status: "NONE" });
      return true;
    } catch {
      setRecoveryView({ status: "UNAVAILABLE" });
      return false;
    }
  };

  const refreshOrder = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["session-order", order.sessionId] }),
      queryClient.invalidateQueries({ queryKey: ["tables"] }),
    ]);
  };

  const persistRecovery = (next: PackageOperationRecovery): boolean => {
    try {
      localStorage.setItem(recoveryKey(operator), JSON.stringify(next));
      setRecoveryView({ status: "OWNED", recovery: next });
      return true;
    } catch {
      setFeedbackKind("ERROR");
      setFeedback("本机无法保存套餐防重复记录，本次没有发出请求。请恢复浏览器存储后重试。");
      return false;
    }
  };

  const confirmSuccess = async (message: string) => {
    const cleared = clearRecovery();
    setCancelDraft(null);
    await refreshOrder();
    setFeedbackKind(cleared ? "SUCCESS" : "ERROR");
    setFeedback(
      cleared
        ? message
        : `${message} 但本机无法清除防重复记录；请恢复浏览器存储并重新打开台单，不要再次执行。`,
    );
  };

  const recoverOriginal = async (owned: PackageOperationRecovery, retryAfterNotFound = true) => {
    if (pending || owned.sessionId !== order.sessionId) return;
    setPending(true);
    setFeedbackKind("INFO");
    setFeedback(`正在先查询“${owned.packageName}”原操作结果…`);
    try {
      if (owned.kind === "ADD") {
        await getStorePackageAddResult(owned.sessionId, owned.command.idempotencyKey);
        await confirmSuccess(`${owned.packageName} 已确认整包加入台单，商品库存只扣减一次。`);
      } else {
        await getStorePackageReturnResult(owned.sessionId, owned.command.idempotencyKey);
        await confirmSuccess(
          `${owned.packageName} 已确认整包撤销，台单应收和库存处置已经一次完成。`,
        );
      }
    } catch (queryError) {
      if (
        retryAfterNotFound &&
        queryError instanceof ApiRequestError &&
        queryError.statusCode === 404
      ) {
        try {
          if (owned.kind === "ADD") {
            await addStorePackageOrderItem(owned.sessionId, owned.command);
            await confirmSuccess(
              `${owned.packageName} 已整包加入台单，商品库存已经按冻结数量扣减。`,
            );
          } else {
            await cancelUnpaidStorePackageOrderItem(
              owned.sessionId,
              owned.packageItemId,
              owned.command,
            );
            await confirmSuccess(
              `${owned.packageName} 已整包撤销，台单应收和库存处置已经一次完成。`,
            );
          }
          return;
        } catch (retryError) {
          if (isTerminalPackageMutationError(retryError)) clearRecovery();
          setFeedbackKind("ERROR");
          setFeedback(
            isTerminalPackageMutationError(retryError)
              ? businessErrorMessage(retryError, "原套餐操作未执行，请刷新台单后重新办理")
              : `${businessErrorMessage(retryError, "原套餐操作结果仍未确认")} 已保留同一请求号；请继续查询原结果。`,
          );
          return;
        }
      }
      setFeedbackKind("ERROR");
      setFeedback(
        `${businessErrorMessage(queryError, "暂时无法确认原套餐操作结果")} 已保留同一请求号，不会自动发起第二笔。`,
      );
    } finally {
      setPending(false);
    }
  };

  const addPackage = async (record: StorePackageRecord) => {
    if (
      !canOperate ||
      pending ||
      disabled ||
      recoveryView.status !== "NONE" ||
      expectedOrderVersion === null
    )
      return;
    const next: PackageOperationRecovery = {
      kind: "ADD",
      actorId: operator.id,
      sessionId: order.sessionId,
      packageName: record.name,
      command: {
        packageId: record.id,
        expectedPackageVersion: record.version,
        expectedOrderVersion,
        idempotencyKey: `store-package-add-${crypto.randomUUID()}`,
      },
    };
    if (!persistRecovery(next)) return;
    setPending(true);
    setFeedbackKind("INFO");
    setFeedback(`正在整包加入“${record.name}”并核对所有商品库存…`);
    try {
      await addStorePackageOrderItem(next.sessionId, next.command);
      await confirmSuccess(`${record.name} 已整包加入台单，所有商品库存已经一次扣减。`);
    } catch (error) {
      if (isTerminalPackageMutationError(error)) clearRecovery();
      setFeedbackKind("ERROR");
      setFeedback(
        isTerminalPackageMutationError(error)
          ? businessErrorMessage(error, "套餐没有加入台单，请刷新后重新选择")
          : `${businessErrorMessage(error, "套餐加单结果尚未确认")} 原请求已保留；请先查询结果，不要重复点击。`,
      );
    } finally {
      setPending(false);
    }
  };

  const cancelPackage = async (item: StorePackageOrderItem, reason: string) => {
    if (
      !canOperate ||
      pending ||
      disabled ||
      recoveryView.status !== "NONE" ||
      expectedOrderVersion === null
    )
      return;
    const normalizedReason = reason.trim();
    if (normalizedReason.length < 2) {
      setFeedbackKind("ERROR");
      setFeedback("请填写至少两个字的整包撤销原因，说明顾客为什么不再购买这个套餐。");
      return;
    }
    const next: PackageOperationRecovery = {
      kind: "CANCEL",
      actorId: operator.id,
      sessionId: order.sessionId,
      packageName: item.packageName,
      packageItemId: item.id,
      command: {
        expectedOrderVersion,
        reason: normalizedReason,
        idempotencyKey: `store-package-cancel-${crypto.randomUUID()}`,
      },
    };
    if (!persistRecovery(next)) return;
    setPending(true);
    setFeedbackKind("INFO");
    setFeedback(`正在整包撤销“${item.packageName}”并核对台单与库存…`);
    try {
      await cancelUnpaidStorePackageOrderItem(next.sessionId, item.id, next.command);
      await confirmSuccess(`${item.packageName} 已整包撤销，台单应收和商品库存已经一次处理。`);
    } catch (error) {
      if (isTerminalPackageMutationError(error)) clearRecovery();
      setFeedbackKind("ERROR");
      setFeedback(
        isTerminalPackageMutationError(error)
          ? businessErrorMessage(error, "套餐没有撤销，请刷新台单后重新办理")
          : `${businessErrorMessage(error, "套餐撤销结果尚未确认")} 原请求已保留；请先查询结果，不要重复恢复库存。`,
      );
    } finally {
      setPending(false);
    }
  };

  const unpaidOrder =
    order.status === "OPEN" &&
    order.paidFen === 0 &&
    order.refundedFen === 0 &&
    order.payments.length === 0 &&
    order.refunds.length === 0;

  return (
    <section className="store-package-order-panel" aria-labelledby={titleId}>
      <header className="subsection-heading">
        <div>
          <h3 id={titleId}>门店套餐</h3>
          <p>一次加入全部商品和本单服务；任一受管商品库存不足，整包都不会加入。</p>
        </div>
      </header>
      {feedback ? (
        <div
          className={`inline-operation-feedback is-${feedbackKind.toLowerCase()}`}
          role={feedbackKind === "ERROR" ? "alert" : "status"}
        >
          {feedbackKind === "ERROR" ? <AlertTriangle size={16} /> : <PackageCheck size={16} />}
          <span>{feedback}</span>
        </div>
      ) : null}
      {recovery ? (
        <button
          className="button secondary"
          type="button"
          disabled={pending || recovery.sessionId !== order.sessionId}
          onClick={() => void recoverOriginal(recovery)}
        >
          <RefreshCw size={16} />
          {pending ? "正在查询原结果…" : `查询“${recovery.packageName}”原结果`}
        </button>
      ) : null}
      {expectedOrderVersion === null ? (
        <div className="disabled-reason" role="alert">
          当前台单版本无法安全确认，套餐操作已暂停；请刷新台单后再试，单点商品记录不会因此改变。
        </div>
      ) : null}

      {(order.packageItems ?? []).length > 0 ? (
        <div className="order-package-lines">
          {(order.packageItems ?? []).map((item) => (
            <article key={item.id} className={item.status === "RETURNED" ? "is-returned" : ""}>
              <div>
                <strong>{item.packageName}</strong>
                <small>
                  {item.lineNumber} · 第 {item.packageVersion} 版
                </small>
                <p>{componentSummary(item)}</p>
              </div>
              <div className="order-package-price">
                <strong>{formatFen(item.receivableFen)}</strong>
                {item.discountFen > 0 ? <small>已优惠 {formatFen(item.discountFen)}</small> : null}
                <span>{item.status === "ACTIVE" ? "本单有效" : "已整包退回"}</span>
              </div>
              {item.status === "ACTIVE" && canOperate ? (
                unpaidOrder ? (
                  cancelDraft?.itemId === item.id ? (
                    <div className="order-package-cancel">
                      <label>
                        整包撤销原因
                        <input
                          value={cancelDraft.reason}
                          maxLength={200}
                          disabled={
                            pending ||
                            disabled ||
                            recoveryView.status !== "NONE" ||
                            expectedOrderVersion === null
                          }
                          placeholder="例如：顾客改为单点商品"
                          onChange={(event) =>
                            setCancelDraft({ itemId: item.id, reason: event.target.value })
                          }
                        />
                      </label>
                      <p>
                        确认后，本套餐应收、全部商品库存和本单服务权益会一次撤销，不能只退其中一项。
                      </p>
                      <div className="button-row">
                        <button
                          className="button danger"
                          type="button"
                          disabled={
                            pending ||
                            disabled ||
                            recoveryView.status !== "NONE" ||
                            expectedOrderVersion === null
                          }
                          onClick={() => void cancelPackage(item, cancelDraft.reason)}
                        >
                          <RotateCcw size={16} /> 确认整包撤销
                        </button>
                        <button
                          className="button secondary"
                          type="button"
                          onClick={() => setCancelDraft(null)}
                        >
                          暂不撤销
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="button secondary"
                      type="button"
                      disabled={
                        pending ||
                        disabled ||
                        recoveryView.status !== "NONE" ||
                        expectedOrderVersion === null
                      }
                      onClick={() => setCancelDraft({ itemId: item.id, reason: "" })}
                    >
                      <RotateCcw size={16} /> 整包撤销
                    </button>
                  )
                ) : (
                  <div className="disabled-reason">
                    这张台单已经付款或正在付款。整包退回请到审批中心，批准后按原付款退款；这里不会直接改金额或库存。
                  </div>
                )
              ) : null}
            </article>
          ))}
        </div>
      ) : null}

      {canOperate ? (
        packagesQuery.isPending ? (
          <div className="compact-empty">正在读取前台可选套餐…</div>
        ) : packagesQuery.isError ? (
          <div className="disabled-reason">套餐目录读取失败；商品单点、转台和结账不受影响。</div>
        ) : (packagesQuery.data ?? []).length === 0 ? (
          <div className="compact-empty">当前没有已启用的门店套餐；仍可继续单点商品。</div>
        ) : (
          <div className="store-package-picker">
            {(packagesQuery.data ?? []).map((record) => (
              <article key={record.id}>
                <div>
                  <strong>{record.name}</strong>
                  <p>{componentSummary(record)}</p>
                  <small>
                    原价 {formatFen(record.listPriceFen)} · 优惠 {formatFen(record.discountFen)} ·
                    当前第 {record.version} 版
                  </small>
                </div>
                <button
                  className="button primary"
                  type="button"
                  disabled={
                    pending ||
                    disabled ||
                    recoveryView.status !== "NONE" ||
                    expectedOrderVersion === null ||
                    order.status !== "OPEN"
                  }
                  onClick={() => void addPackage(record)}
                >
                  <PackagePlus size={16} /> 加入 {formatFen(record.salePriceFen)}
                </button>
              </article>
            ))}
          </div>
        )
      ) : (
        <div className="disabled-reason">
          当前岗位可查看套餐记录；加入或撤销套餐请由本店店长或收银员办理。
        </div>
      )}
    </section>
  );
}
